import { getPermissions } from '@/apis/user';
import { NAVIGATION_PERMISSIONS } from '@/constants/permissions';
import { computed, ref } from 'vue';

interface UserPermissions {
  permissions: string[];
  roles: string[];
}

interface AxiosResponse {
  data: UserPermissions;
  status: number;
  statusText: string;
  headers: any;
  config: any;
  request: any;
}

const userPermissions = ref<UserPermissions | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);

export const usePermissions = () => {
  // Load permissions from localStorage on initialization
  const loadStoredPermissions = () => {
    const stored = localStorage.getItem('userPermissions');
    
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        userPermissions.value = parsed;
      } catch (err) {
        // Error handled silently
      }
    }
  };

  // Initialize permissions from localStorage on first use
  if (!userPermissions.value) {
    loadStoredPermissions();
  }

  // Fetch user permissions from API
  const fetchPermissions = async () => {
    try {
      isLoading.value = true;
      error.value = null;
      
      const response = await getPermissions();
      
      // Handle Axios response structure - extract the actual data
      userPermissions.value = response.data;
      
      // Store in localStorage for persistence (store only the data part)
      localStorage.setItem('userPermissions', JSON.stringify(response.data));
    } catch (err) {
      error.value = 'Failed to fetch permissions';
      
      // Try to load from localStorage as fallback
      const stored = localStorage.getItem('userPermissions');
      if (stored) {
        try {
          userPermissions.value = JSON.parse(stored);
        } catch (parseErr) {
          // Error handled silently
        }
      }
    } finally {
      isLoading.value = false;
    }
  };

  // Initialize permissions (load from storage and fetch if needed)
  const initializePermissions = async () => {
    // First try to load from localStorage
    loadStoredPermissions();
    
    // If no stored permissions, fetch from API
    if (!userPermissions.value) {
      await fetchPermissions();
    }
  };

  // Check if user has specific role
  const hasRole = (role: string): boolean => {
    if (!userPermissions.value || !userPermissions.value.roles) {
      return false;
    }

    return userPermissions.value.roles.includes(role);
  };

  // Check if user is super admin (must be defined before use)
  const isSuperAdmin = computed(() => {
    return hasRole('Role.SuperAdmin');
  });

  // Check if user has specific permission
  const hasPermission = (permission: string): boolean => {
    if (!userPermissions.value || !userPermissions.value.permissions) return false;
    
    // Super admins have all permissions
    if (isSuperAdmin.value) {
      return true;
    }
    
    return userPermissions.value.permissions.includes(permission);
  };

  // Check if user has any of the required permissions
  const hasAnyPermission = (permissions: readonly string[]): boolean => {
    if (!userPermissions.value || !userPermissions.value.permissions) return false;
    
    // Super admins have all permissions
    if (isSuperAdmin.value) {
      return true;
    }
    
    return permissions.some(permission => 
      userPermissions.value!.permissions.includes(permission)
    );
  };

  // Check if user has all required permissions
  const hasAllPermissions = (permissions: string[]): boolean => {
    if (!userPermissions.value || !userPermissions.value.permissions) return false;
    
    // Super admins have all permissions
    if (isSuperAdmin.value) {
      return true;
    }
    
    return permissions.every(permission => 
      userPermissions.value!.permissions.includes(permission)
    );
  };

  // Check if navigation item should be visible
  const isNavigationVisible = (navigationKey: keyof typeof NAVIGATION_PERMISSIONS): boolean => {
    // If permissions are not loaded, return true to show all items initially
    if (!userPermissions.value) {
      return true;
    }
    
    // Super admins can see everything
    if (isSuperAdmin.value) {
      return true;
    }

    if (navigationKey === "DOCTOR_POINTS") {
      return isDoctor.value;
    }
    if (navigationKey === "RESEARCHER_POINTS") {
      return isResearcher.value;
    }

    const requiredPermissions = NAVIGATION_PERMISSIONS[navigationKey];
    if (!requiredPermissions?.length) {
      return false;
    }

    return hasAnyPermission(requiredPermissions);
  };

  // Check if user is admin
  const isAdmin = computed(() => {
    return hasRole('Role.Admin');
  });

  const isDoctor = computed(() => {
    return hasRole('Role.Admin') && !isSuperAdmin.value;
  });

  const isResearcher = computed(() => {
    return hasRole('Role.Researcher');
  });

  // Check if user is delivery
  const isDelivery = computed(() => {
    return hasRole('Role.Delivery');
  });

  // Clear permissions (useful for logout)
  const clearPermissions = () => {
    userPermissions.value = null;
    localStorage.removeItem('userPermissions');
  };

  // Get all user permissions
  const getAllPermissions = computed(() => {
    return userPermissions.value?.permissions || [];
  });

  // Get all user roles
  const getAllRoles = computed(() => {
    return userPermissions.value?.roles || [];
  });

  return {
    // State
    userPermissions: computed(() => userPermissions.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    
    // Actions
    fetchPermissions,
    loadStoredPermissions,
    initializePermissions,
    clearPermissions,
    
    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isNavigationVisible,
    hasRole,
    
    // Role checks
    isSuperAdmin,
    isAdmin,
    isDoctor,
    isResearcher,
    isDelivery,
    
    // Getters
    getAllPermissions,
    getAllRoles,
  };
};
